import type { MiContenidoMetricsExportData } from "../../miContenidoExportData";
import { isChannelNamedProgram } from "../../programPages/channelUmbrellaProgram";
import type { ProgramPagePdfData } from "../../programPages/types";
import {
  METRICS_PDF_DECK_CONTENT_PADDING_BOTTOM_WITH_FOOTER_NOTE,
  METRICS_PDF_DECK_FOOTER_NOTE_BLOCK_PT,
  METRICS_PDF_DECK_PAGE_PADDING_TOP,
  METRICS_PDF_FOOTER_RESERVED_PT,
  METRICS_PDF_PAGE_SIZE,
} from "../constants";
import {
  FACEBOOK_REPORT_PLATFORM,
  orderedPlatformReportPlatforms,
  type PlatformReportId,
} from "./platformReportPlatforms";

/**
 * Deck pages render with `wrap={false}`, so blocks are split up front.
 * Height left for content on a page that carries a footer note.
 */
const CONTENT_HEIGHT_PT =
  METRICS_PDF_PAGE_SIZE[1] -
  METRICS_PDF_DECK_PAGE_PADDING_TOP -
  METRICS_PDF_FOOTER_RESERVED_PT -
  METRICS_PDF_DECK_CONTENT_PADDING_BOTTOM_WITH_FOOTER_NOTE -
  METRICS_PDF_DECK_FOOTER_NOTE_BLOCK_PT;

/** Title + table header + section margins. */
const TABLE_CHROME_PT = 50;
/** Row min-height plus vertical padding, rounded up so a row is not clipped. */
const ROW_PT = 32;

export const PLATFORM_CONTENT_ITEMS_PER_PAGE = Math.max(
  1,
  Math.floor((CONTENT_HEIGHT_PT - TABLE_CHROME_PT) / ROW_PT),
);

export type PlatformSummaryMetric = {
  id: PlatformReportId;
  label: string;
  contentCount: number;
  totalViews: number;
  avgViews: number;
  totalInteractions: number;
  avgInteractions: number;
};

export type PlatformReportContentItem = {
  id: string;
  platformId: PlatformReportId;
  title: string;
  programId?: string;
  programName: string;
  channelName?: string;
  publishedAt: string | null;
  views: number;
  interactions: number;
  url?: string;
};

export type PlatformReportContentInput = {
  items: PlatformReportContentItem[];
  /** Platforms whose list stopped at the fetch cap. */
  truncatedPlatformIds?: readonly PlatformReportId[];
};

export type PlatformContentPage = {
  platformId: PlatformReportId;
  label: string;
  pageIndex: number;
  pageCount: number;
  totalItems: number;
  totalViews: number;
  truncated: boolean;
  items: PlatformReportContentItem[];
};

export type PlatformReportData = {
  /** One row per platform, in deck order. Facebook only when it has activity. */
  summary: PlatformSummaryMetric[];
  /** Program names that contributed to the summary, for the Emprende footnote. */
  programNames: string[];
  contentPages: PlatformContentPage[];
};

function avgPerContent(total: number, contentCount: number): number {
  if (total > 0 && contentCount > 0) return Math.round(total / contentCount);
  return 0;
}

function isChannelRow(
  name: string,
  channelName: string | undefined,
  podcastTitle: string,
  isChannelProgram?: boolean,
): boolean {
  if (isChannelProgram === true) return true;
  if (isChannelProgram === false) return false;
  return isChannelNamedProgram(name, channelName?.trim() || podcastTitle);
}

function rowTotals(page: ProgramPagePdfData, surface: string): {
  contentCount: number;
  totalViews: number;
  totalInteractions: number;
} {
  const row = page.metricRows.find((metric) => metric.surface === surface);
  return {
    contentCount: Math.round(Number(row?.contentCount) || 0),
    totalViews: Math.round(Number(row?.totalViews) || 0),
    totalInteractions: Math.round(Number(row?.totalInteractions) || 0),
  };
}

function hasActivity(metric: {
  contentCount: number;
  totalViews: number;
  totalInteractions: number;
}): boolean {
  return (
    metric.contentCount > 0 ||
    metric.totalViews > 0 ||
    metric.totalInteractions > 0
  );
}

/** Newest first. Rows without a date stay at the end. */
function compareContentByDate(
  a: PlatformReportContentItem,
  b: PlatformReportContentItem,
): number {
  if (a.publishedAt !== b.publishedAt) {
    if (!a.publishedAt) return 1;
    if (!b.publishedAt) return -1;
    return b.publishedAt.localeCompare(a.publishedAt);
  }
  return a.title.localeCompare(b.title, "es");
}

function publishedDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

export function paginatePlatformContent(
  items: PlatformReportContentItem[],
): PlatformReportContentItem[][] {
  if (items.length === 0) return [];
  const pages: PlatformReportContentItem[][] = [];
  for (let index = 0; index < items.length; index += PLATFORM_CONTENT_ITEMS_PER_PAGE) {
    pages.push(items.slice(index, index + PLATFORM_CONTENT_ITEMS_PER_PAGE));
  }
  return pages;
}

export function buildPlatformReportData(
  data: MiContenidoMetricsExportData,
  platformIds: readonly PlatformReportId[],
  content?: PlatformReportContentInput,
): PlatformReportData {
  const selected = orderedPlatformReportPlatforms(platformIds);
  const podcastTitle = data.titles?.podcastTitle?.trim() ?? "";
  const pages = (data.programPages ?? []).filter(
    (page) =>
      !isChannelRow(page.name, page.channelName, podcastTitle, page.isChannelProgram),
  );

  const facebookTotals = pages.reduce(
    (sum, page) => {
      const row = rowTotals(page, FACEBOOK_REPORT_PLATFORM.surface);
      return {
        contentCount: sum.contentCount + row.contentCount,
        totalViews: sum.totalViews + row.totalViews,
        totalInteractions: sum.totalInteractions + row.totalInteractions,
      };
    },
    { contentCount: 0, totalViews: 0, totalInteractions: 0 },
  );
  const platforms = hasActivity(facebookTotals)
    ? selected.concat(FACEBOOK_REPORT_PLATFORM)
    : selected;

  if (platforms.length === 0) {
    return { summary: [], programNames: [], contentPages: [] };
  }

  const includedProgramIds = new Set<string>();
  const programNames: string[] = [];
  const summary: PlatformSummaryMetric[] = [];

  for (const platform of platforms) {
    const totals = { contentCount: 0, totalViews: 0, totalInteractions: 0 };
    for (const page of pages) {
      const row = rowTotals(page, platform.surface);
      if (!hasActivity(row)) continue;
      includedProgramIds.add(page.programId);
      totals.contentCount += row.contentCount;
      totals.totalViews += row.totalViews;
      totals.totalInteractions += row.totalInteractions;
    }
    if (!hasActivity(totals)) continue;
    summary.push({
      id: platform.id,
      label: platform.label,
      contentCount: totals.contentCount,
      totalViews: totals.totalViews,
      avgViews: avgPerContent(totals.totalViews, totals.contentCount),
      totalInteractions: totals.totalInteractions,
      avgInteractions: avgPerContent(totals.totalInteractions, totals.contentCount),
    });
  }

  for (const page of pages) {
    if (includedProgramIds.has(page.programId)) programNames.push(page.name);
  }

  const truncated = new Set<string>(content?.truncatedPlatformIds ?? []);
  const contentPages: PlatformContentPage[] = [];

  for (const platform of summary) {
    const fromEpisodes: PlatformReportContentItem[] =
      platform.id === "youtube_videos"
        ? pages.flatMap((page) => {
            if (!includedProgramIds.has(page.programId)) return [];
            return (page.episodes ?? []).map((episode) => ({
              id: episode.contentId,
              platformId: "youtube_videos" as const,
              title: episode.title,
              programId: page.programId,
              programName: page.name,
              ...(page.channelName ? { channelName: page.channelName } : {}),
              publishedAt: publishedDay(episode.publishedAt),
              views: Math.round(Number(episode.views) || 0),
              interactions: Math.round(Number(episode.interactions) || 0),
              ...(episode.url ? { url: episode.url } : {}),
            }));
          })
        : [];

    const fromFetch = (content?.items ?? []).filter(
      (item) =>
        item.platformId === platform.id &&
        platform.id !== "youtube_videos" &&
        (!item.programId || includedProgramIds.has(item.programId)),
    );

    const items = fromEpisodes.concat(fromFetch).sort(compareContentByDate);
    if (items.length === 0) continue;

    const totalViews = items.reduce((sum, item) => sum + item.views, 0);
    const slices = paginatePlatformContent(items);
    slices.forEach((slice, index) => {
      contentPages.push({
        platformId: platform.id,
        label: platform.label,
        pageIndex: index + 1,
        pageCount: slices.length,
        totalItems: items.length,
        totalViews,
        truncated: truncated.has(platform.id),
        items: slice,
      });
    });
  }

  return { summary, programNames, contentPages };
}
